import React, { useState, useEffect } from "react";
import { Check, ArrowRight, Zap, Calendar, Download, CreditCard, X, CheckCircle, AlertCircle, Info, RefreshCw } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useSubscription } from "../hooks/useSubscription";
import { useNotifications } from "../hooks/useNotifications";
import { useBillingHistory } from "../hooks/useBillingHistory";
import Loader, { InlineLoader, ButtonLoader } from "../components/ui/Loader";
import SupportModal from "../components/ui/SupportModal";
import socketService from "../services/socketService";
import { PLANS as CANONICAL_PLANS, TRIAL_DAYS } from "../config/plans";
import { trackCheckoutInitiated } from "../utils/analytics";

// Notification Modal Component
const NotificationModal = ({ notification, onClose }) => {
    useEffect(() => {
        if (notification && notification.type === "success") {
            // Auto-dismiss success notifications after 3 seconds
            const timer = setTimeout(() => {
                onClose();
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [notification, onClose]);

    if (!notification) return null;

    const getIcon = (type) => {
        switch (type) {
            case "success":
                return <CheckCircle className="w-6 h-6 text-green-500" />;
            case "error":
                return <AlertCircle className="w-6 h-6 text-red-500" />;
            case "info":
            default:
                return <Info className="w-6 h-6 text-blue-500" />;
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-[#111] rounded-[20px] p-6 max-w-md w-full mx-4 shadow-2xl border border-gray-200 dark:border-white/[0.06]">
                <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">{getIcon(notification.type)}</div>
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{notification.title}</h3>
                        <p className="text-gray-500 dark:text-gray-500 mb-6">{notification.message}</p>
                        <div className="flex justify-end">
                            <button
                                onClick={onClose}
                                className="rounded-full bg-black dark:bg-white text-white dark:text-black px-6 py-2 font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
                            >
                                {notification.type === "success" ? "Continue" : "Got it"}
                            </button>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-[10px] p-1 ml-4 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>
        </div>
    );
};

// Confirm Plan Change Modal Component
const ConfirmPlanChangeModal = ({
    isOpen,
    onClose,
    onConfirm,
    currentPlan,
    newPlan,
    isAnnual,
    proration,
    isProcessing,
    plans
}) => {
    if (!isOpen) return null;

    const planRank = { starter: 1, basic: 2, pro: 3 };
    const currentRank = currentPlan ? planRank[String(currentPlan.plan).toLowerCase()] : null;
    const newRank = newPlan ? planRank[String(newPlan.name).toLowerCase()] : null;

    const isUpgrade = currentPlan && newPlan &&
        ((currentRank && newRank && newRank > currentRank) ||
         (currentRank === newRank && currentPlan.billing === 'month' && isAnnual));

    const isDowngrade = currentPlan && newPlan &&
        ((currentRank && newRank && newRank < currentRank) ||
         (currentRank === newRank && currentPlan.billing === 'year' && !isAnnual));

    const actionText = isUpgrade ? 'Upgrade' : isDowngrade ? 'Downgrade' : 'Change';
    const actionColor = isUpgrade ? 'text-green-600 dark:text-green-400' :
                       isDowngrade ? 'text-orange-600 dark:text-orange-400' :
                       'text-blue-600 dark:text-blue-400';

    // Get current plan details from plans array
    const currentPlanObj = plans?.find(p => p.name.toLowerCase() === currentPlan?.plan?.toLowerCase());
    const currentPlanPrice = currentPlan?.billing === 'year' ? currentPlanObj?.annualPrice : currentPlanObj?.monthlyPrice;
    const currentPlanDisplay = currentPlan?.billing === 'year'
        ? `$${Math.round(currentPlanPrice / 12)}/month (billed annually)`
        : `$${currentPlanPrice}/month`;

    const newPlanPrice = isAnnual ? newPlan.annualPrice : newPlan.monthlyPrice;
    const billingPeriod = isAnnual ? 'year' : 'month';
    const displayPrice = isAnnual ? `$${Math.round(newPlanPrice / 12)}/month (billed annually)` : `$${newPlanPrice}/month`;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-[#111] rounded-[20px] p-8 max-w-lg w-full mx-4 shadow-2xl border border-gray-200 dark:border-white/[0.06]">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <h2 className={`text-2xl font-bold ${actionColor} mb-2`}>
                            {actionText} to {newPlan.name} Plan?
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-500">
                            Review the changes before confirming
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isProcessing}
                        className="text-gray-400 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-[10px] p-1 disabled:opacity-50 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Current vs New Plan */}
                <div className="rounded-[12px] border border-gray-100 dark:border-white/[0.06] p-6 mb-6">
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-500 mb-1">Current Plan</p>
                            <p className="font-semibold text-gray-900 dark:text-white capitalize">
                                {currentPlan?.plan || 'None'}
                            </p>
                            {currentPlan && currentPlanPrice && (
                                <p className="text-sm text-gray-500 dark:text-gray-500">
                                    {currentPlanDisplay}
                                </p>
                            )}
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-500 mb-1">New Plan</p>
                            <p className="font-semibold text-gray-900 dark:text-white">
                                {newPlan.name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-500">
                                {displayPrice}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Proration Details */}
                {proration && (
                    <div className="border border-gray-100 dark:border-white/[0.06] rounded-[12px] p-6 mb-6">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Billing Details</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500 dark:text-gray-500">
                                    Credit for unused time ({proration.daysRemaining} days)
                                </span>
                                <span className="text-green-600 dark:text-green-400 font-medium">
                                    -${proration.credit.toFixed(2)}
                                </span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500 dark:text-gray-500">
                                    {newPlan.name} plan ({proration.daysRemaining} days)
                                </span>
                                <span className="text-gray-900 dark:text-white font-medium">
                                    +${proration.charge.toFixed(2)}
                                </span>
                            </div>
                            <div className="border-t border-gray-100 dark:border-white/[0.06] pt-3 flex justify-between">
                                <span className="font-semibold text-gray-900 dark:text-white">
                                    {proration.total >= 0 ? 'Due now' : 'Credit applied'}
                                </span>
                                <span className={`font-bold text-lg ${proration.total >= 0 ? 'text-gray-900 dark:text-white' : 'text-green-600 dark:text-green-400'}`}>
                                    ${Math.abs(proration.total).toFixed(2)}
                                </span>
                            </div>
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-600 mt-4">
                            Next billing date: {proration.nextBillingDate} (${newPlanPrice}/{billingPeriod})
                        </p>
                    </div>
                )}

                {/* Important Notice */}
                <div className="rounded-[12px] bg-blue-500/[0.05] border border-blue-500/20 p-4 mb-6">
                    <div className="flex items-start space-x-3">
                        <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-900 dark:text-blue-200">
                            <p className="font-medium mb-1">What happens next?</p>
                            <ul className="list-disc list-inside space-y-1 text-blue-800 dark:text-blue-300">
                                <li>Your subscription updates immediately</li>
                                {proration && proration.total >= 0 ? (
                                    <li>You'll be charged ${proration.total.toFixed(2)} now (prorated difference)</li>
                                ) : proration && proration.total < 0 ? (
                                    <li>Credit of ${Math.abs(proration.total).toFixed(2)} will be applied to your account</li>
                                ) : (
                                    <li>Billing will be adjusted based on the plan change</li>
                                )}
                                <li>{isUpgrade ? 'New features are' : 'Your plan is'} available right away</li>
                                <li>Your billing cycle continues uninterrupted</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3">
                    <button
                        onClick={onClose}
                        disabled={isProcessing}
                        className="flex-1 rounded-full bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white px-6 py-3 font-medium hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isProcessing}
                        className="flex-1 rounded-full bg-black dark:bg-white text-white dark:text-black px-6 py-3 font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                        {isProcessing ? (
                            <>
                                <ButtonLoader />
                                <span className="ml-2">Processing...</span>
                            </>
                        ) : (
                            <>
                                {proration && proration.total >= 0 ? (
                                    <>Confirm {actionText} - ${proration.total.toFixed(2)}</>
                                ) : (
                                    <>Confirm {actionText}</>
                                )}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

const BillingPage = () => {
    const { user, refreshUser } = useAuth();
    const { subscription, hasActiveSubscription, getDaysUntilExpiry, isTrialing, isPastDue } = useSubscription();
    const { createTestNotification, refresh: refreshNotifications } = useNotifications();
    const [currentPlan, setCurrentPlan] = useState(null);
    const [userPlan, setUserPlan] = useState(null);
    const [isAnnual, setIsAnnual] = useState(false);
    const [initialToggleSet, setInitialToggleSet] = useState(false);
    const planLimits = Object.fromEntries(CANONICAL_PLANS.map((p) => [p.slug, p.limits.conversations]));
    const [usage, setUsage] = useState({
        current: 0,
        limit: planLimits[userPlan?.plan?.toLowerCase()] || planLimits.starter,
    });
    const [paymentReady, setPaymentReady] = useState(true);
    const [loadingPlan, setLoadingPlan] = useState(true);
    const [isCreatingTransaction, setIsCreatingTransaction] = useState(false);

    // Prices, limits, trial eligibility and the "popular" flag come from
    // src/config/plans.js so this page can never drift from the landing page again.
    const PLAN_COPY = {
        starter: {
            description: "For solo founders trying it out",
            extraFeatures: ["Website integration", "Email support"],
        },
        basic: {
            description: "Perfect for growing businesses",
            extraFeatures: ["Analytics dashboard", "Transcript CSV export", "Email support"],
        },
        pro: {
            description: "For teams that need it all",
            extraFeatures: [
                "Remove PaperBrainz branding",
                "Advanced analytics",
                "Priority onboarding support",
                "Transcript CSV export",
            ],
        },
    };

    const plans = CANONICAL_PLANS.map((plan) => ({
        slug: plan.slug,
        name: plan.name,
        monthlyPrice: plan.monthlyPrice,
        annualPrice: plan.annualTotal,
        popular: plan.popular,
        trial: plan.trial,
        description: PLAN_COPY[plan.slug].description,
        features: [
            `${plan.limits.conversations.toLocaleString()} conversations/month`,
            plan.limits.sources === -1
                ? "Unlimited knowledge sources"
                : `Up to ${plan.limits.sources} knowledge sources`,
            `${plan.limits.characters.toLocaleString()} characters in sources`,
            ...PLAN_COPY[plan.slug].extraFeatures,
        ],
    }));

    const getPrice = (plan) => {
        if (plan.monthlyPrice === "Custom") return "Custom";
        return isAnnual ? plan.annualPrice : plan.monthlyPrice;
    };

    const getDisplayPrice = (plan) => {
        if (plan.monthlyPrice === "Custom") return "Custom";
        if (isAnnual) {
            return `$${Math.round(plan.annualPrice / 12)}`;
        }
        return `$${plan.monthlyPrice}`;
    };

    // Helper function to check if a plan is the user's current plan (including billing interval)
    const isCurrentPlan = (plan) => {
        if (!currentPlan || !userPlan) return false;

        const planMatches = currentPlan.toLowerCase() === plan.name.toLowerCase();
        const billingMatches = (
            (isAnnual && userPlan.billing === 'year') ||
            (!isAnnual && userPlan.billing === 'month')
        );

        return planMatches && billingMatches;
    };

    // Get dynamic button text based on current plan
    const getPlanButtonText = (plan) => {
        const planName = plan.name;
        const rank = { starter: 1, basic: 2, pro: 3 };

        // First-time purchase: surface the free trial where it applies.
        // LS only grants the trial on a customer's first subscription, so we only
        // show "free trial" wording when the user has no current plan.
        if (!currentPlan) {
            return plan.trial ? `Start ${TRIAL_DAYS}-day free trial` : `Get ${planName}`;
        }

        if (isCurrentPlan(plan)) {
            return "Current Plan";
        }

        const currentRank = rank[currentPlan.toLowerCase()];
        const targetRank = rank[planName.toLowerCase()];

        if (currentRank && targetRank) {
            if (targetRank > currentRank) return `Upgrade to ${planName}`;
            if (targetRank < currentRank) return `Downgrade to ${planName}`;
        }

        return `Get ${planName}`;
    };

    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [notification, setNotification] = useState(null);
    const [isDownloadingAll, setIsDownloadingAll] = useState(false);
    const [showSupportModal, setShowSupportModal] = useState(false);
    const [showConfirmPlanChangeModal, setShowConfirmPlanChangeModal] = useState(false);
    const [pendingPlanChange, setPendingPlanChange] = useState(null);

    // Billing history hook with real-time updates
    const {
        payments: billingHistory,
        invoices,
        summary: billingSummary,
        loading: loadingBillingHistory,
        hasMore: hasMoreBillingHistory,
        refresh: refreshBillingHistory,
        loadMore: loadMoreBillingHistory,
        downloadInvoice,
        getTotalSpent,
        getSuccessfulPayments
    } = useBillingHistory(10, true);
    const [downloadingInvoices, setDownloadingInvoices] = useState(new Set());

    // Helper function to show notifications
    const showNotification = (type, title, message) => {
        setNotification({ type, title, message });
    };

    const closeNotification = () => {
        setNotification(null);
    };

    // Calculate proration for plan change
    const calculateProration = (newPlan, newBillingIsAnnual) => {
        if (!userPlan || !subscription?.currentPeriodEnd) {
            return null;
        }

        // Get current plan pricing
        const currentPlanObj = plans.find(p => p.name.toLowerCase() === userPlan.plan.toLowerCase());
        if (!currentPlanObj) return null;

        const currentPrice = userPlan.billing === 'year' ? currentPlanObj.annualPrice : currentPlanObj.monthlyPrice;
        const newPrice = newBillingIsAnnual ? newPlan.annualPrice : newPlan.monthlyPrice;

        // Calculate days remaining in current billing period
        const now = new Date();
        const periodEnd = new Date(subscription.currentPeriodEnd);
        const daysRemaining = Math.max(0, Math.ceil((periodEnd - now) / (1000 * 60 * 60 * 24)));

        // Calculate total days in billing period
        const currentBillingDays = userPlan.billing === 'year' ? 365 : 30;
        const newBillingDays = newBillingIsAnnual ? 365 : 30;

        // Calculate proration amounts
        const credit = (currentPrice / currentBillingDays) * daysRemaining;
        const charge = (newPrice / newBillingDays) * daysRemaining;
        const total = charge - credit;

        return {
            daysRemaining,
            credit,
            charge,
            total,
            nextBillingDate: periodEnd.toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
            })
        };
    };


    const handleCancelSubscription = async (cancelImmediately = false) => {
        try {
            setIsCancelling(true);
            const token = localStorage.getItem("accessToken");
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/lemon-squeezy/cancel-subscription`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'true'
                },
                body: JSON.stringify({ cancelImmediately }),
            });

            const data = await response.json();

            if (data.success) {
                showNotification("success", "Subscription Cancelled", data.message);
                setShowCancelModal(false);
                // Refresh user data, plan, billing history, and notifications
                await fetchUserPlan();
                await refreshBillingHistory();
                await refreshNotifications();
            } else {
                showNotification("error", data.error || "Cancellation Failed", data.message || "Failed to cancel subscription");
            }
        } catch (error) {
            showNotification("error", "Cancellation Failed", "Failed to cancel subscription. Please try again.");
        } finally {
            setIsCancelling(false);
        }
    };


    const handleDownloadInvoice = async (transactionId) => {
        try {
            setDownloadingInvoices((prev) => new Set(prev).add(transactionId));

            // Find the payment by transaction ID
            const payment = billingHistory.find(p => p.transactionId === transactionId);

            if (payment) {
                const success = await downloadInvoice(payment);
                if (success) {
                    showNotification("success", "Invoice Downloaded", "Your invoice has been opened in a new tab.");
                } else {
                    showNotification("error", "Download Failed", "Invoice not available for this payment.");
                }
            } else {
                showNotification("error", "Payment Not Found", "Could not find payment details.");
            }

        } catch (error) {
            showNotification("error", "Download Failed", "Failed to download invoice. Please try again.");
        } finally {
            setDownloadingInvoices((prev) => {
                const newSet = new Set(prev);
                newSet.delete(transactionId);
                return newSet;
            });
        }
    };

    const handleDownloadAllInvoices = async () => {
        try {
            setIsDownloadingAll(true);

            // Get all successful payments with available invoices
            const paymentsWithInvoices = billingHistory.filter(payment =>
                payment.status === 'succeeded' &&
                (payment.invoice?.receipt_url || payment.transactionDetails?.receipt_url)
            );

            if (paymentsWithInvoices.length === 0) {
                showNotification("info", "No Invoices", "No downloadable invoices found.");
                return;
            }

            // Download each invoice
            let successCount = 0;
            for (const payment of paymentsWithInvoices) {
                const success = await downloadInvoice(payment);
                if (success) successCount++;
                // Add small delay between downloads
                await new Promise(resolve => setTimeout(resolve, 500));
            }

            if (successCount > 0) {
                showNotification(
                    "success",
                    "Invoices Downloaded",
                    `Successfully opened ${successCount} invoice${successCount > 1 ? 's' : ''} in new tabs.`
                );
            } else {
                showNotification("error", "Download Failed", "No invoices could be downloaded.");
            }
        } catch (error) {
            showNotification("error", "Download Failed", "Failed to download invoices. Please try again.");
        } finally {
            setIsDownloadingAll(false);
        }
    };

    // Function to fetch user plan data
    const fetchUserPlan = async () => {
        try {
            setLoadingPlan(true);
            const userData = await refreshUser();
            setUserPlan(userData.currentPlan);
            setCurrentPlan(userData.currentPlan?.plan || null);
            return userData;
        } catch (error) {
            return null;
        } finally {
            setLoadingPlan(false);
        }
    };

    // Sync subscription with LS on mount, then fetch plan
    useEffect(() => {
        if (user) {
            const token = localStorage.getItem("accessToken");
            fetch(`${process.env.REACT_APP_API_URL}/api/subscription/sync`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'true'
                }
            })
                .then(r => r.json())
                .then(syncResult => {
                    fetchUserPlan();
                })
                .catch(err => {
                    fetchUserPlan();
                });
        }
    }, [user?.id]);

    // Fetch real usage data
    useEffect(() => {
        if (userPlan?.plan) {
            const token = localStorage.getItem("accessToken");
            fetch(`${process.env.REACT_APP_API_URL}/api/usage`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'true'
                }
            })
                .then(r => {
                    return r.json();
                })
                .then(data => {
                    if (data.success) {
                        setUsage({
                            current: data.data.usage.messagesThisMonth || 0,
                            limit: data.data.limits.messagesPerMonth || planLimits[userPlan.plan.toLowerCase()] || 2000
                        });
                    }
                })
                .catch(err => {});
        }
    }, [userPlan?.plan]);

    // Set annual toggle to match user's current billing interval
    useEffect(() => {
        if (userPlan?.billing && !initialToggleSet) {
            setIsAnnual(userPlan.billing === 'year');
            setInitialToggleSet(true);
        }
    }, [userPlan?.billing]);

    // Payment system is ready (Lemon Squeezy uses URL redirect, no client SDK needed)
    useEffect(() => {
        setPaymentReady(true);
    }, []);

    // Listen for real-time subscription updates to refresh the billing page
    useEffect(() => {
        if (!user) return;

        let unsubscribeSubscription;
        let unsubscribePayment;

        const handleSubscriptionUpdate = async (data) => {
            if (data.type === 'subscription_created' || data.type === 'subscription_updated') {
                await fetchUserPlan();
                await refreshNotifications();
            }
        };

        const handlePaymentUpdate = async (data) => {
            if (data.type === 'payment_completed') {
                await fetchUserPlan();
                await refreshBillingHistory();
                await refreshNotifications();
            }
        };

        // Connect to socket and subscribe to events
        const connectAndSubscribe = async () => {
            try {
                await socketService.connect();
                unsubscribeSubscription = socketService.on('subscription_updated', handleSubscriptionUpdate);
                unsubscribePayment = socketService.on('payment_updated', handlePaymentUpdate);
            } catch (error) {
                // Socket connection failed silently
            }
        };

        connectAndSubscribe();

        // Cleanup function - properly unsubscribe when component unmounts
        return () => {
            if (unsubscribeSubscription) unsubscribeSubscription();
            if (unsubscribePayment) unsubscribePayment();
        };
    }, [user?.id]);

    // Handle confirmed plan change
    const handleConfirmPlanChange = async () => {
        if (!pendingPlanChange) return;

        const { plan, billingInterval } = pendingPlanChange;
        setIsCreatingTransaction(true);

        try {
            const token = localStorage.getItem("accessToken");

            const changeResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/lemon-squeezy/change-subscription`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'true'
                },
                body: JSON.stringify({
                    planType: plan.name.toLowerCase(),
                    billingInterval: billingInterval
                }),
            });

            const changeData = await changeResponse.json();

            if (changeData.success) {
                // Determine action text for notification
                const rank = { starter: 1, basic: 2, pro: 3 };
                const currentRank = rank[currentPlan.toLowerCase()];
                const targetRank = rank[plan.name.toLowerCase()];
                const actionText = currentRank && targetRank && targetRank > currentRank
                    ? 'upgraded to'
                    : currentRank && targetRank && targetRank < currentRank
                    ? 'downgraded to'
                    : 'changed to';

                showNotification("success", "Subscription Updated", `Your plan has been ${actionText} ${plan.name} successfully!`);

                // Refresh user data, billing history, and notifications
                await fetchUserPlan();
                await refreshBillingHistory();
                await refreshNotifications();

                // Close modal and reset state
                setShowConfirmPlanChangeModal(false);
                setPendingPlanChange(null);
                setIsCreatingTransaction(false);
            } else {
                showNotification("error", "Subscription Change Failed", changeData.message || "Unable to change subscription. Please try again.");
                setShowConfirmPlanChangeModal(false);
                setPendingPlanChange(null);
                setIsCreatingTransaction(false);
            }
        } catch (changeError) {
            showNotification("error", "Subscription Change Failed", "An error occurred while changing your subscription. Please try again.");
            setShowConfirmPlanChangeModal(false);
            setPendingPlanChange(null);
            setIsCreatingTransaction(false);
        }
    };

    const handleUpgrade = async (plan) => {
        if (!paymentReady) {
            showNotification("error", "Payment System Error", "Payment system not ready. Please try again.");
            return;
        }

        trackCheckoutInitiated(
            plan.slug,
            isAnnual ? "annual" : "monthly",
            isAnnual ? plan.annualPrice : plan.monthlyPrice,
        );

        // If user has an existing subscription, show confirmation modal first
        if (currentPlan && currentPlan.toLowerCase() !== plan.name.toLowerCase()) {
            const billingInterval = isAnnual ? 'annual' : 'monthly';
            const proration = calculateProration(plan, isAnnual);

            setPendingPlanChange({
                plan,
                billingInterval,
                proration
            });

            setShowConfirmPlanChangeModal(true);
            return;
        }

        // No existing subscription - create checkout and redirect
        setIsCreatingTransaction(true);

        try {
            const token = localStorage.getItem("accessToken");

            const getPriceId = (planName, billing) => {
                return `${planName.toLowerCase()}_${billing}`;
            };

            const selectedBilling = isAnnual ? "annual" : "monthly";
            const priceId = getPriceId(plan.name, selectedBilling);

            // Create Lemon Squeezy checkout
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/lemon-squeezy/checkout`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'true'
                },
                body: JSON.stringify({ priceId }),
            });

            const data = await response.json();

            if (data.success && data.data.checkoutUrl) {
                // Redirect to Lemon Squeezy checkout page
                window.location.href = data.data.checkoutUrl;
            } else {
                showNotification("error", "Payment Setup Failed", "Unable to initialize payment. Please try again.");
            }
        } catch (error) {
            showNotification("error", "Payment Setup Failed", "An error occurred. Please try again.");
        } finally {
            setIsCreatingTransaction(false);
        }
    };

    // Handle URL parameters for success/cancel redirects
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('success') === 'true') {
            showNotification("success", "Payment Successful!", "Your subscription has been created successfully.");
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);

            // Wait for backend to process webhook, then refresh
            const refreshData = async () => {
                await new Promise(resolve => setTimeout(resolve, 2000));
                await fetchUserPlan();
                await refreshBillingHistory();
                await refreshNotifications();
            };
            refreshData();
        } else if (urlParams.get('canceled') === 'true') {
            showNotification("info", "Payment Canceled", "Your payment was canceled. You can try again anytime.");
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    // Auto-checkout flow from landing CTA for already-logged-in users.
    // URL shape: /dashboard/billing?autoCheckout=starter&billing=monthly
    useEffect(() => {
        if (!paymentReady) return;
        const urlParams = new URLSearchParams(window.location.search);
        const autoPlan = urlParams.get('autoCheckout')?.toLowerCase();
        const autoBilling = (urlParams.get('billing') || 'monthly').toLowerCase();
        if (!autoPlan) return;

        const targetPlan = plans.find(p => p.name.toLowerCase() === autoPlan);
        if (!targetPlan) return;

        // Strip params so a refresh doesn't re-trigger
        window.history.replaceState({}, document.title, window.location.pathname);
        setIsAnnual(autoBilling === 'annual');
        handleUpgrade(targetPlan);
    }, [paymentReady]);

    return (
        <div className="container-mobile py-4 sm:py-6 md:py-8 w-full">
            {/* Header */}
            <div className="mb-8 sm:mb-12 md:mb-16">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-light tracking-tight text-gray-900 dark:text-white mb-2 sm:mb-3 md:mb-4">Billing</h1>
                <p className="text-base sm:text-lg md:text-xl text-gray-500 dark:text-gray-500 font-light">Simple, transparent pricing that grows with you.</p>
            </div>

            {/* Current Plan Overview */}
            <div className="mb-8 sm:mb-12 md:mb-16">
                <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] p-4 sm:p-6 md:p-8">
                    {loadingPlan ? (
                        <InlineLoader text="Loading plan information..." />
                    ) : !userPlan ? (
                        // No plan - show message to purchase a plan
                        <div className="text-center py-8 sm:py-10 md:py-12">
                            <div className="mb-4 sm:mb-6">
                                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gray-50 dark:bg-white/[0.04] rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                                    <CreditCard className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-gray-400 dark:text-gray-500" />
                                </div>
                                <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-1.5 sm:mb-2">No Active Plan</h2>
                                <p className="text-sm sm:text-base text-gray-500 dark:text-gray-500 mb-4 sm:mb-5 md:mb-6">Choose a plan below to get started with our service.</p>
                                <div className="inline-flex items-center rounded-[12px] bg-yellow-500/[0.05] border border-yellow-500/20 text-orange-700 dark:text-orange-300 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium">
                                    <span>Service access is limited without an active subscription</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Has plan - show current plan details
                        <>
                            <div className="mb-6 sm:mb-8">
                                <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-5">Current Plan</h2>
                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6">
                                    {/* Left: Plan info */}
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 sm:gap-3 flex-wrap mb-1">
                                            <span className="text-base sm:text-lg font-medium text-gray-900 dark:text-white capitalize">{userPlan.plan} Plan</span>
                                            <span
                                                className={`px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                                                    userPlan.status === "active"
                                                        ? "bg-green-500/[0.08] text-green-600"
                                                        : userPlan.status === "trialing"
                                                        ? "bg-blue-500/[0.08] text-blue-600"
                                                        : userPlan.status === "canceled_at_period_end"
                                                        ? "bg-yellow-500/[0.08] text-yellow-600"
                                                        : userPlan.status === "canceled"
                                                        ? "bg-red-500/[0.08] text-red-600"
                                                        : "bg-yellow-500/[0.08] text-yellow-600"
                                                }`}
                                            >
                                                {userPlan.status === "trialing"
                                                    ? "Trial"
                                                    : userPlan.status === "canceled_at_period_end"
                                                    ? "Ending Soon"
                                                    : userPlan.status === "canceled"
                                                    ? "Canceled"
                                                    : userPlan.status}
                                            </span>
                                        </div>
                                        <p className="text-sm sm:text-base text-gray-500 dark:text-gray-500">
                                            {(() => {
                                                const planObj = plans.find(p => p.name.toLowerCase() === userPlan.plan?.toLowerCase());
                                                const amount = userPlan.amount > 0
                                                    ? userPlan.amount
                                                    : planObj
                                                        ? (userPlan.billing === 'year' ? planObj.annualPrice : planObj.monthlyPrice)
                                                        : 0;
                                                return `$${amount}/${userPlan.billing === 'year' ? 'year' : 'month'}`;
                                            })()}
                                        </p>
                                    </div>

                                    {/* Right: Billing date + Cancel */}
                                    <div className="flex flex-col items-start sm:items-end gap-3 sm:gap-4">
                                        <div className="text-left sm:text-right">
                                            <div className="flex items-center text-gray-500 dark:text-gray-500 mb-1 sm:justify-end">
                                                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                                                <span className="text-xs sm:text-sm">
                                                    {userPlan?.status === "canceled_at_period_end"
                                                        ? "Subscription ends"
                                                        : "Next billing date"}
                                                </span>
                                            </div>
                                            <p className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">
                                                {(() => {
                                                    if (!hasActiveSubscription()) {
                                                        return "No active subscription";
                                                    }
                                                    if (!subscription?.currentPeriodEnd) {
                                                        return "Loading...";
                                                    }

                                                    const nextDate = new Date(subscription.currentPeriodEnd);
                                                    const isToday = nextDate.toDateString() === new Date().toDateString();

                                                    if (isToday) {
                                                        if (userPlan?.status === "canceled_at_period_end") {
                                                            return "Subscription ends today";
                                                        }
                                                        return isTrialing() ? "Trial ends today" : "Renews today";
                                                    }

                                                    return nextDate.toLocaleDateString("en-US", {
                                                        year: "numeric",
                                                        month: "long",
                                                        day: "numeric",
                                                    });
                                                })()}
                                            </p>
                                            {subscription?.currentPeriodEnd && hasActiveSubscription() && (
                                                <p className="text-xs text-gray-400 dark:text-gray-600 mt-0.5">
                                                    {(() => {
                                                        const daysLeft = getDaysUntilExpiry();
                                                        if (userPlan?.status === "canceled_at_period_end") {
                                                            if (daysLeft === 0) {
                                                                return "Subscription ends today";
                                                            } else if (daysLeft === 1) {
                                                                return "Subscription ends tomorrow";
                                                            } else if (daysLeft > 0) {
                                                                return `Subscription ends in ${daysLeft} days`;
                                                            } else {
                                                                return "Subscription ended";
                                                            }
                                                        } else if (daysLeft === 0) {
                                                            return isTrialing() ? "Trial expires today" : "Renews today";
                                                        } else if (daysLeft === 1) {
                                                            return isTrialing() ? "Trial ends tomorrow" : "Renews tomorrow";
                                                        } else if (daysLeft > 0) {
                                                            return isTrialing() ? `Trial ends in ${daysLeft} days` : `Renews in ${daysLeft} days`;
                                                        } else {
                                                            return isPastDue() ? "Payment overdue" : "Expired";
                                                        }
                                                    })()}
                                                </p>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => setShowCancelModal(true)}
                                            className="rounded-full border border-red-300 dark:border-red-500/40 text-red-600 dark:text-red-400 px-4 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors btn-touch"
                                        >
                                            Cancel Subscription
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Usage */}
                            <div className="rounded-[12px] border border-gray-100 dark:border-white/[0.06] p-4 sm:p-5 md:p-6">
                                <div className="flex items-center justify-between mb-3 sm:mb-4">
                                    <h3 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">Usage This Month</h3>
                                    <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-500">
                                        {usage.current.toLocaleString()} of {usage.limit.toLocaleString()}
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-white/[0.08] rounded-full h-1.5 sm:h-2">
                                    <div
                                        className="bg-gray-900 dark:bg-gray-100 h-1.5 sm:h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${(usage.current / usage.limit) * 100}%` }}
                                    />
                                </div>
                                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 mt-1.5 sm:mt-2">
                                    {Math.round((usage.current / usage.limit) * 100)}% of monthly limit used
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Plans */}
            <div className="mb-8 sm:mb-12 md:mb-16">
                <div className="text-center mb-12 sm:mb-14 md:mb-16">
                    <h2 className="text-3xl sm:text-4xl md:text-[2.5rem] font-semibold tracking-tight text-gray-900 dark:text-white mb-3 sm:mb-4">Choose your plan</h2>
                    <p className="text-base md:text-lg text-gray-500 dark:text-gray-500 font-light mb-6 sm:mb-8 max-w-xl mx-auto">Upgrade or downgrade anytime. No long-term contracts.</p>

                    {/* Annual/Monthly Toggle */}
                    <div className="flex items-center justify-center">
                        <div className="relative flex items-center rounded-full p-1 border bg-gray-100 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06]">
                            <button
                                onClick={() => setIsAnnual(false)}
                                className={`relative z-10 px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                                    !isAnnual
                                        ? "bg-black dark:bg-white text-white dark:text-black"
                                        : "text-gray-500 dark:text-gray-400"
                                }`}
                            >
                                Monthly
                            </button>
                            <div className="relative">
                                <span className="absolute -top-3 right-0 translate-x-1/3 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap z-20 bg-gray-900 dark:bg-white text-white dark:text-black">
                                    Save 17%
                                </span>
                                <button
                                    onClick={() => setIsAnnual(true)}
                                    className={`relative z-10 px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                                        isAnnual
                                            ? "bg-black dark:bg-white text-white dark:text-black"
                                            : "text-gray-500 dark:text-gray-400"
                                    }`}
                                >
                                    Annual
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-5 max-w-6xl mx-auto">
                    {plans.map((plan) => {
                        const isCurrent = isCurrentPlan(plan);
                        const perMonthDisplay = isAnnual
                            ? `$${(plan.annualPrice / 12).toFixed(2).replace(/\.00$/, "")}`
                            : `$${plan.monthlyPrice}`;
                        return (
                            <div
                                key={plan.name}
                                className={`relative p-6 sm:p-8 rounded-[20px] border transition-all flex flex-col h-full ${
                                    isCurrent
                                        ? "border-green-400/60 dark:border-green-500/40 bg-green-50/40 dark:bg-green-500/[0.04]"
                                        : plan.popular
                                        ? "border-gray-900 dark:border-white/30 bg-white dark:bg-white/[0.04] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)] dark:shadow-[0_20px_60px_-20px_rgba(255,255,255,0.08)]"
                                        : "border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.02]"
                                }`}
                            >
                                {plan.popular && !isCurrent && (
                                    <div className="absolute -top-3.5 left-1/2 transform -translate-x-1/2">
                                        <span className="px-4 py-1.5 rounded-full text-xs font-medium bg-black text-white dark:bg-white dark:text-black">
                                            Most Popular
                                        </span>
                                    </div>
                                )}
                                {isCurrent && (
                                    <div className="absolute -top-3.5 left-1/2 transform -translate-x-1/2">
                                        <span className="px-4 py-1.5 rounded-full text-xs font-medium bg-green-500 text-white flex items-center gap-1.5">
                                            <Check className="w-3 h-3" />
                                            Current Plan
                                        </span>
                                    </div>
                                )}

                                <div className="mb-8">
                                    <h3 className="text-lg font-medium mb-1 text-gray-900 dark:text-white">{plan.name}</h3>
                                    <p className="mb-5 text-sm font-light text-gray-500 dark:text-gray-500">{plan.description}</p>
                                    <div className="flex items-baseline">
                                        <span className="text-5xl font-semibold tracking-tight tabular-nums text-gray-900 dark:text-white">
                                            {perMonthDisplay}
                                        </span>
                                        <span className="ml-2 text-sm font-light text-gray-500 dark:text-gray-500">/month</span>
                                    </div>
                                    {isAnnual && (
                                        <p className="text-sm font-light mt-1 text-gray-400 dark:text-gray-600">
                                            ${plan.annualPrice} billed annually
                                        </p>
                                    )}
                                    {plan.trial && !currentPlan && (
                                        <p className="text-xs font-light mt-3 text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                                            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                                            {TRIAL_DAYS}-day free trial · cancel anytime
                                        </p>
                                    )}
                                </div>

                                <ul className="space-y-3 mb-8">
                                    {plan.features.map((feature, featureIndex) => (
                                        <li key={featureIndex} className="flex items-start space-x-3">
                                            <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-500 dark:text-gray-400" />
                                            <span className="text-sm font-light text-gray-600 dark:text-gray-400">{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-auto">
                                    {isCurrent ? (
                                        <button
                                            disabled
                                            className="w-full py-3 rounded-full font-medium text-sm bg-green-500/[0.1] text-green-600 dark:text-green-400 cursor-default"
                                        >
                                            Current Plan
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleUpgrade(plan)}
                                            disabled={!paymentReady || isCreatingTransaction}
                                            className={`w-full py-3 rounded-full font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                                                plan.popular
                                                    ? "bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200"
                                                    : "text-gray-900 dark:text-white border border-gray-200 dark:border-white/[0.08] hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                                            }`}
                                        >
                                            {isCreatingTransaction ? (
                                                <>
                                                    <span className="mr-2"><ButtonLoader /></span>
                                                    Loading...
                                                </>
                                            ) : !paymentReady ? (
                                                "Loading..."
                                            ) : (
                                                getPlanButtonText(plan)
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Billing History */}
            {userPlan && (
                <div className="mb-8 sm:mb-12 md:mb-16">
                    <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06]">
                        <div className="px-5 sm:px-6 md:px-8 py-4 sm:py-5 border-b border-gray-100 dark:border-white/[0.06]">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                                <div className="min-w-0">
                                    <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-1.5 sm:mb-2">Billing History</h2>
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                                        <p className="text-sm sm:text-base text-gray-500 dark:text-gray-500">Download your invoices and view payment history</p>
                                        {billingSummary && (
                                            <div className="text-xs sm:text-sm text-gray-400 dark:text-gray-600">
                                                {billingSummary.totalPayments} payments &bull; ${billingSummary.totalAmount.toFixed(2)} total
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
                                    <button
                                        onClick={refreshBillingHistory}
                                        disabled={loadingBillingHistory}
                                        className="flex items-center gap-1.5 sm:gap-2 text-gray-500 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed btn-touch px-2 py-1 sm:px-0 sm:py-0"
                                        title="Refresh billing history"
                                    >
                                        <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${loadingBillingHistory ? 'animate-spin' : ''}`} />
                                        <span className="text-xs sm:text-sm font-medium">Refresh</span>
                                    </button>
                                    <button
                                        onClick={handleDownloadAllInvoices}
                                        disabled={isDownloadingAll || billingHistory.length === 0}
                                        className="flex items-center gap-1.5 sm:gap-2 text-gray-500 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed btn-touch px-2 py-1 sm:px-0 sm:py-0"
                                    >
                                        {isDownloadingAll ? <ButtonLoader /> : <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                                        <span className="text-xs sm:text-sm font-medium">{isDownloadingAll ? "Downloading..." : "Download All"}</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-white/[0.03]">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-500 whitespace-nowrap">Invoice</th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-500 whitespace-nowrap">Date</th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-500 whitespace-nowrap">Amount</th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-500 whitespace-nowrap">Status</th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-500 whitespace-nowrap">Download</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-white/[0.06]">
                                    {loadingBillingHistory ? (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-12 text-center">
                                                <InlineLoader text="Loading billing history..." />
                                            </td>
                                        </tr>
                                    ) : billingHistory.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-12 text-center">
                                                <div className="text-gray-400 dark:text-gray-600">
                                                    <Calendar className="w-8 h-8 mx-auto mb-2" />
                                                    <p>No billing history yet</p>
                                                    <p className="text-sm mt-1">Your invoices will appear here after your first payment</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        billingHistory.map((payment) => (
                                            <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-white/[0.03]">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="font-medium text-gray-900 dark:text-white">
                                                        {payment.description || "Subscription Payment"}
                                                    </div>
                                                    <div className="text-sm text-gray-500 dark:text-gray-500 mt-0.5">
                                                        {payment.invoice?.invoice_number ? (
                                                            <div>Invoice #{payment.invoice.invoice_number}</div>
                                                        ) : (
                                                            <div>Transaction #{payment.transactionId?.slice(-8) || "N/A"}</div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                                    {(() => {
                                                        try {
                                                            const date = new Date(payment.createdAt);
                                                            if (isNaN(date.getTime())) return 'Invalid Date';
                                                            return date.toLocaleDateString("en-US", {
                                                                year: "numeric",
                                                                month: "short",
                                                                day: "numeric",
                                                            });
                                                        } catch (error) {
                                                            return 'Invalid Date';
                                                        }
                                                    })()}
                                                </td>
                                                <td className="px-6 py-4 font-medium text-gray-900 dark:text-white whitespace-nowrap">${payment.amount?.toFixed(2)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span
                                                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                                            payment.status === "succeeded"
                                                                ? "bg-green-500/[0.08] text-green-600"
                                                                : "bg-red-500/[0.08] text-red-600"
                                                        }`}
                                                    >
                                                        {payment.status === "succeeded" ? "Paid" : "Failed"}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {payment.invoiceUrl ? (
                                                        <a
                                                            href={payment.invoiceUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center gap-1.5 text-gray-500 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
                                                        >
                                                            <Download className="w-4 h-4" />
                                                            <span className="text-sm">PDF</span>
                                                        </a>
                                                    ) : (
                                                        <span className="text-sm text-gray-400 dark:text-gray-600">N/A</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Load More Button */}
                        {hasMoreBillingHistory && !loadingBillingHistory && (
                            <div className="px-6 py-4 border-t border-gray-100 dark:border-white/[0.06] text-center">
                                <button
                                    onClick={loadMoreBillingHistory}
                                    className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors text-sm font-medium"
                                >
                                    <span>Load More</span>
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* FAQ or Additional Info */}
            <div className="text-center">
                <p className="text-sm sm:text-base text-gray-500 dark:text-gray-500">
                    Need help choosing a plan?
                    <button
                        onClick={() => setShowSupportModal(true)}
                        className="text-gray-900 dark:text-white font-medium hover:underline ml-1 btn-touch"
                    >
                        Contact our team
                    </button>
                </p>
            </div>


            {/* Cancel Subscription Modal */}
            {showCancelModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-[#111] rounded-[20px] p-6 max-w-md w-full mx-4 shadow-2xl border border-gray-200 dark:border-white/[0.06]">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Cancel Subscription</h2>
                            <button onClick={() => setShowCancelModal(false)} className="text-gray-400 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-[10px] p-1 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <p className="text-gray-500 dark:text-gray-500 mb-6">
                            Are you sure you want to cancel your subscription? This action will cancel your subscription immediately.
                        </p>

                        <div className="space-y-3">
                            <button
                                onClick={() => handleCancelSubscription(true)}
                                disabled={isCancelling}
                                className="w-full rounded-full bg-red-500 hover:bg-red-600 text-white px-4 py-3 font-medium transition-colors disabled:opacity-50"
                            >
                                {isCancelling ? (
                                    <>
                                        <span className="inline mr-2">
                                            <ButtonLoader />
                                        </span>
                                        Cancelling...
                                    </>
                                ) : (
                                    "Cancel Subscription"
                                )}
                            </button>

                            <button
                                onClick={() => setShowCancelModal(false)}
                                disabled={isCancelling}
                                className="w-full rounded-full bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white px-4 py-3 font-medium hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors disabled:opacity-50"
                            >
                                Keep Subscription
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Notification Modal */}
            <NotificationModal notification={notification} onClose={closeNotification} />

            {/* Confirm Plan Change Modal */}
            <ConfirmPlanChangeModal
                isOpen={showConfirmPlanChangeModal}
                onClose={() => {
                    setShowConfirmPlanChangeModal(false);
                    setPendingPlanChange(null);
                }}
                onConfirm={handleConfirmPlanChange}
                currentPlan={userPlan}
                newPlan={pendingPlanChange?.plan}
                isAnnual={isAnnual}
                proration={pendingPlanChange?.proration}
                isProcessing={isCreatingTransaction}
                plans={plans}
            />

            {/* Support Modal */}
            <SupportModal
                isOpen={showSupportModal}
                onClose={() => setShowSupportModal(false)}
            />

        </div>
    );
};

export default BillingPage;
